import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ExamSessionFormDialog from "@/components/ExamSessionFormDialog";
import GradeDialog from "@/components/GradeDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Trash2, Calendar, MapPin, Users, Award, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const GRADE_LABEL = { "30L": "30L", idoneo: "Idoneo", non_idoneo: "Non idoneo" };

export default function ExamSessionDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [gradeFor, setGradeFor] = useState(null);
  const [delOpen, setDelOpen] = useState(false);

  const { data: session } = useQuery({
    queryKey: ["exam-session", id],
    queryFn: async () => (await api.get(`/exams/sessions/${id}`)).data,
  });

  const editMut = useMutation({
    mutationFn: async (p) => (await api.patch(`/exams/sessions/${id}`, p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["exam-session", id] }); toast.success("Appello aggiornato"); setEditOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const gradeMut = useMutation({
    mutationFn: async (p) => (await api.post(`/exams/grades`, { session_id: id, student_id: gradeFor.student_id, ...p })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["exam-session", id] }); toast.success("Voto verbalizzato"); setGradeFor(null); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delMut = useMutation({
    mutationFn: async () => (await api.delete(`/exams/sessions/${id}`)).data,
    onSuccess: () => { toast.success("Appello eliminato"); window.location.href = "/scuola/esami"; },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setDelOpen(false); },
  });
  const cancelBookingMut = useMutation({
    mutationFn: async (bid) => (await api.delete(`/exams/bookings/${bid}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["exam-session", id] }); toast.success("Prenotazione annullata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (!session) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to="/scuola/esami" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna agli appelli
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-500">
            {session.area_name && (
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: session.area_color }}/>{session.area_name}
              </span>
            )}
            {session.course_year_level && <span>· {session.course_year_level}° anno</span>}
            {session.course_cfu && <span>· {session.course_cfu} CFU</span>}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#1C352D] mt-1">{session.course_name}</h1>
          <div className="text-sm text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
            <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 rounded-full text-[10px]">{session.status}</Badge>
            {session.is_historical && <Badge className="bg-amber-100 text-amber-900 border border-amber-200 rounded-full text-[10px]" data-testid="detail-historical-badge">STORICO</Badge>}
            {session.year_academic && <Badge className="bg-stone-100 text-stone-700 border border-stone-200 rounded-full text-[10px] mono">A.A. {session.year_academic}</Badge>}
            <span className="inline-flex items-center gap-1"><Calendar size={12}/> {fmtDate(session.date)} {session.time || ""}</span>
            {session.room && <span className="inline-flex items-center gap-1"><MapPin size={12}/> {session.room}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} className="rounded-full" data-testid="exam-edit-btn">
            <Pencil size={13} className="mr-1.5"/> Modifica
          </Button>
          <Button variant="outline" onClick={() => setDelOpen(true)} className="rounded-full text-red-700 border-red-300 hover:bg-red-50">
            <Trash2 size={13} className="mr-1.5"/> Elimina
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Users size={11}/> Prenotati</div>
          <div className="text-2xl font-semibold mt-1">{session.bookings_count}{session.max_students ? `/${session.max_students}` : ""}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Award size={11}/> Voti verbalizzati</div>
          <div className="text-2xl font-semibold mt-1">{session.grades_count}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Commissione</div>
          <div className="text-sm mt-1 text-stone-700">{session.examiners?.join(", ") || "—"}</div>
        </Card>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-5 border-b border-stone-100 text-sm font-medium flex items-center justify-between">
          <span>Iscritti e voti</span>
          <span className="text-xs text-stone-500 font-normal">{session.bookings?.length || 0} totali · {session.grades_count} voti</span>
        </div>
        {!session.bookings || session.bookings.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">Nessuno iscritto ancora.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {session.bookings.filter((b) => b.status === "booked" || b.status === "graded_only").map((b, i) => (
              <li key={b.id || `syn-${b.student_id}`} data-testid={`booking-${b.id || b.student_id}`} className="p-4 flex items-center gap-4 hover:bg-stone-50 animate-in-row" style={{ animationDelay: `${i * 15}ms` }}>
                <div className="w-9 h-9 rounded-full bg-[#1C352D] text-white grid place-items-center text-xs font-semibold shrink-0">
                  {(b.student_name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {b.student_name}
                    {b.synthetic && <Badge className="bg-stone-100 text-stone-600 border border-stone-200 text-[10px] rounded-full">solo voto</Badge>}
                  </div>
                  <div className="text-[11px] text-stone-500 mono">{b.matricola} · {b.year_level}° anno</div>
                </div>
                {b.grade ? (
                  <div className="flex items-center gap-2">
                    <Badge className={`text-sm rounded-full font-semibold ${b.grade.is_passed ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                      <CheckCircle2 size={12} className="mr-1"/>{GRADE_LABEL[b.grade.grade_value] || b.grade.grade_value}
                    </Badge>
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => setGradeFor(b)} data-testid={`grade-edit-${b.student_id}`}>Modifica</Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setGradeFor(b)} data-testid={`grade-btn-${b.id}`} className="rounded-full text-xs bg-[#E2725B] hover:bg-[#c85d47]">
                      <Award size={12} className="mr-1"/> Verbalizza
                    </Button>
                    {!b.synthetic && (
                      <Button size="sm" variant="outline" className="text-xs rounded-full text-red-700 border-red-300 hover:bg-red-50" onClick={() => cancelBookingMut.mutate(b.id)}>
                        Annulla
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ExamSessionFormDialog open={editOpen} onOpenChange={setEditOpen} mode="edit" initial={session}
        submitting={editMut.isPending} onSubmit={(p) => editMut.mutate(p)}/>
      <GradeDialog open={!!gradeFor} onOpenChange={(v) => !v && setGradeFor(null)} student={gradeFor} initial={gradeFor?.grade}
        submitting={gradeMut.isPending} onSubmit={(p) => gradeMut.mutate(p)}/>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l&apos;appello?</AlertDialogTitle>
            <AlertDialogDescription>Le prenotazioni vengono rimosse. Non è possibile eliminare se ci sono voti verbalizzati.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={() => delMut.mutate()}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
